import { DISPLAY_NAME_MAX_LENGTH, isGenericDisplayName, sanitizeParticipantPhotoURL } from './authIdentity';
import { isEventLive } from './eventStatus';
import { isIntentionalLeave } from './sessionHistory';

/**
 * Pure identity helpers for Phase 0.
 * Authorization is derived from Firebase Auth uid + event.hostId.
 */

export function isEventHost(userId, hostId) {
  return Boolean(userId && hostId && userId === hostId);
}

export function findParticipant(participants, userId) {
  if (!userId || !Array.isArray(participants)) return null;
  return participants.find((participant) => participant.id === userId) || null;
}

/** Historical membership: host or an existing participant row. Not live occupancy. */
export function canResumeEvent(userId, eventData) {
  if (!userId || !eventData) return false;
  if (userId === eventData.hostId) return true;
  return Boolean(findParticipant(eventData.participants, userId));
}

/**
 * Live-room entry. Historical membership is not enough after Leave Party.
 * Ended events still use canResumeEvent so recap remains available.
 */
export function canEnterLiveSession(userId, eventData, sessionEntry) {
  if (!canResumeEvent(userId, eventData)) return false;
  if (!isEventLive(eventData)) return true;
  if (userId === eventData.hostId) return true;
  return !isIntentionalLeave(sessionEntry);
}

/**
 * If this uid is the host and their stored participant name is still a generic
 * fallback, replace that row with the current Google display name. Other rows
 * are left untouched so guests do not appear rewritten.
 */
export function buildReconciledHostParticipants(participants, {
  uid,
  hostId,
  name,
  photoURL,
} = {}) {
  if (!uid || uid !== hostId || isGenericDisplayName(name) || !Array.isArray(participants)) {
    return null;
  }

  let changed = false;
  const next = participants.map((participant) => {
    if (participant?.id !== uid) return participant;
    if (!isGenericDisplayName(participant.name) && participant.name === name) {
      return participant;
    }
    if (!isGenericDisplayName(participant.name)) return participant;
    changed = true;
    return {
      ...participant,
      name,
      photoURL: photoURL !== undefined ? photoURL : participant.photoURL,
    };
  });

  return changed ? next : null;
}

/**
 * Reuse the existing guest row (same uid) and refresh its display identity.
 * Never creates a second row, never rewrites the host, never promotes role.
 */
export function reconcileOwnGuestParticipant(participants, {
  uid,
  hostId,
  name,
  photoURL,
} = {}) {
  if (!uid || !hostId || uid === hostId || !Array.isArray(participants)) return null;

  const trimmed = typeof name === 'string' ? name.trim().slice(0, DISPLAY_NAME_MAX_LENGTH) : '';
  if (!trimmed) return null;

  const existing = findParticipant(participants, uid);
  if (!existing || existing.id === hostId || existing.role === 'host') return null;

  const sanitizedPhoto = photoURL !== undefined ? sanitizeParticipantPhotoURL(photoURL) : null;
  const nextPhoto = sanitizedPhoto || existing.photoURL || null;
  const nextRole = existing.role === 'subhost' ? 'subhost' : 'guest';
  if (existing.name === trimmed && (existing.photoURL ?? null) === nextPhoto) return null;

  return participants.map((row) => {
    if (row?.id !== uid) return row;
    return {
      id: uid,
      name: trimmed,
      role: nextRole,
      joinedAt: row.joinedAt,
      photoURL: nextPhoto,
    };
  });
}

/**
 * Host if uid matches event.hostId.
 * Otherwise honor an existing subhost participant role; everyone else is guest.
 * No role until both uid and hostId are known.
 */
export function deriveUserRole(userId, hostId, participantRole = null) {
  if (!userId || !hostId) return null;
  if (userId === hostId) return 'host';
  if (participantRole === 'subhost') return 'subhost';
  return 'guest';
}

const HOST_ONLY = new Set([
  'canDeleteItems',
  'canPromoteToSubHost',
  'canRemoveGuest',
  'canUpdateSettings',
]);

const HOST_OR_SUBHOST = new Set([
  'canReorderQueue',
  'canSkip',
  'canPause',
]);

const EVERYONE = new Set(['canRequestSong', 'canChat', 'canReact']);

export function canPerform(action, { userId, hostId, userRole } = {}) {
  const role = deriveUserRole(userId, hostId, userRole);

  if (EVERYONE.has(action)) return true;
  if (action === 'canVoteSkip') return role === 'guest';
  if (HOST_ONLY.has(action)) return role === 'host';
  if (HOST_OR_SUBHOST.has(action)) return role === 'host' || role === 'subhost';
  return false;
}
